/* eslint-disable react-hooks/exhaustive-deps */
import "./styles.css";

import {
  Button,
  Col,
  Form,
  FormInstance,
  Input,
  Modal,
  notification,
  Select,
  Space,
  Typography,
} from "antd";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AutoSizer, { Size } from "react-virtualized-auto-sizer";
import { FixedSizeList as List } from "react-window";

import { ExclamationCircleOutlined } from "@ant-design/icons";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { RootState } from "../../../app/store";
import {
  activeProviderListAsync,
  updateActiveProviderFilter,
} from "../../../features/provider/activeProviderListSlice";
import { assignProviderToUserRequestAsync } from "../../../features/userRequest/assingProviderToUserRequestSlice";
import { createAndAssignProvider } from "../../../services/adminApi";
import { assignProviderToUserRequests } from "../../../services/dispatcherApi";
import { InlineForm } from "../../../shared/components/InlineForm";
import {
  CorporativoDescription,
  FirstNameDescription,
  LastNameDescription,
  MobileDescription,
} from "../../../shared/constants/Resources";
import { WebRoutes } from "../../../shared/constants/WebRoutes";
import ProviderDTO from "../../../shared/dtos/providerDTO";
import ServiceTypeDTO from "../../../shared/dtos/serviceTypeDTO";
import UserRequestDto from "../../../shared/dtos/userRequestDTO";
import { COUNTRIES } from "../../../utils/constants/countries";
import getDestinationsByUserRequestHelper from "../../../utils/helpers/getDestinationsByUserRequestHelper";
import ContainerLayout from "../../common/containerLayout";
import { DispatcherMap } from "../../map/Map";
import { ProviderCard } from "./providerCard";

const { Title } = Typography;
const { Search } = Input;

const CALCULATION_TYPE_OPTIONS = [
  { label: "Automático", value: "automatic" },
  { label: "Manual", value: "manual" },
];

export const AssignProvider = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [form] = Form.useForm();

  const [selectedProvider, setSelectedProvider] = useState<ProviderDTO | null>(
    null,
  );
  const [openModal, setOpenModal] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const dispatch = useAppDispatch();
  const {
    value: activeProviderListValue,
    status,
    filter,
  } = useAppSelector((state: RootState) => state.activeProviderList);
  const pendingUserRequestValue: UserRequestDto[] = useAppSelector(
    (state: RootState) => state.pendingUserRequest.value,
  );
  const serviceTypes = useAppSelector(
    (state: RootState) => state.serviceTypeList.value,
  );

  useEffect(() => {
    form.setFieldsValue({
      country: COUNTRIES[0].label,
      serviceTypeId: serviceTypes.find(
        (x: ServiceTypeDTO) =>
          x?.name?.toLowerCase() === pendingUserRequestValue[0]?.serviceTypeId,
      )?.id,
    });

    if (
      pendingUserRequestValue[0]?.serviceTypeId !== filter ||
      !activeProviderListValue.length
    ) {
      dispatch(
        updateActiveProviderFilter(pendingUserRequestValue[0]?.serviceTypeId),
      );

      dispatch(
        activeProviderListAsync({
          serviceTypeId: pendingUserRequestValue[0]?.serviceTypeId,
          orderBy: "completedTrips",
          order: "DESC",
        }),
      );
    }
  }, [dispatch]);

  const onSearch = async (providerName: string) => {
    setSelectedProvider(null);
    dispatch(
      activeProviderListAsync({
        serviceTypeId: pendingUserRequestValue[0]?.serviceTypeId,
        search: providerName,
        orderBy: "completedTrips",
        order: "DESC",
      }),
    );
  };

  const handleSelectedProvider = useCallback(async () => {
    if (!pendingUserRequestValue || !selectedProvider || !selectedProvider.id)
      return;

    const isMultiTrip = state?.package;

    try {
      setConfirmLoading(true);
      if (isMultiTrip) {
        await assignProviderToUserRequests({
          userRequests: pendingUserRequestValue,
          providerId: selectedProvider.id,
        });
      } else {
        await dispatch(
          assignProviderToUserRequestAsync({
            userRequestId: pendingUserRequestValue[0].id,
            providerId: selectedProvider.id,
          }),
        );
      }
    } catch (error) {
      notification.error({
        message: isMultiTrip
          ? "Ha ocurrido un error al asignar el conductor a los viajes seleccionados."
          : "Ha ocurrido un error al asignar el conductor al viaje seleccionado.",
        placement: "bottom",
        key: "global",
      });
      return;
    } finally {
      setConfirmLoading(true);
    }

    notification.success({
      message: isMultiTrip
        ? "El conductor se ha asignado a los viajes seleccionados."
        : "El conductor se ha asignado al viaje seleccionado.",
      placement: "bottom",
      key: "global",
    });

    navigate(WebRoutes.TripListRoute, {
      replace: true,
      state: {
        refreshData: true,
      },
    });
  }, [dispatch, navigate, pendingUserRequestValue, selectedProvider]);

  const handleSelectedProviderFromMap = (provider: ProviderDTO | null) => {
    setSelectedProvider(provider);
  };

  const handleModalOpen = () => {
    setOpenModal((openModal) => !openModal);
  };

  const onFinish = async (values: any) => {
    const { totalTripPrice, ...provider } = values;
    setConfirmLoading(true);
    try {
      await Promise.all(
        pendingUserRequestValue.map(async (p: any) => {
          const email = `${provider.firstName.trim()}_${provider.lastName.trim()}@mowi.external.com`;
          const password = process.env.REACT_APP_ADMIN_DEVICE_TOKEN;
          const serviceTypeId = serviceTypes.find(
            (x: ServiceTypeDTO) =>
              x?.name?.toLowerCase() === CorporativoDescription,
          )?.id;
          return await createAndAssignProvider(
            p.id,
            {
              ...provider,
              email,
              password,
              serviceTypeId,
            },
            totalTripPrice,
          );
        }),
      );

      notification.success({
        message: "El conductor se ha asignado al viaje seleccionado.",
        placement: "bottom",
        key: "global",
      });

      navigate(WebRoutes.TripListRoute, {
        replace: true,
        state: {
          refreshData: true,
        },
      });
    } catch (error) {
      notification.error({
        message:
          "Ha ocurrido un error al asignar el conductor al viaje seleccionado.",
        placement: "bottom",
        key: "global",
      });
    } finally {
      setConfirmLoading(false);
    }
  };

  const TotalTripField = memo(({ form }: { form: FormInstance<any> }) => {
    const calculationType = Form.useWatch("calculationType", form);

    if (calculationType !== "manual") return null;

    return (
      <Form.Item
        label="Total del viaje"
        name="totalTripPrice"
        rules={[{ required: true, message: "Ingrese el total del viaje" }]}
      >
        <Input type="number" placeholder="0.00" />
      </Form.Item>
    );
  });

  const AssignTemporalDriverModal = () => {
    return (
      <Modal
        forceRender
        title="Asignar conductor temporal"
        open={openModal}
        onOk={form.submit}
        confirmLoading={confirmLoading}
        okText={confirmLoading ? "Cargando" : "Crear y asignar"}
        onCancel={handleModalOpen}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          name="temporalDriver"
          initialValues={{
            calculationType: "automatic",
          }}
        >
          <InlineForm>
            <Form.Item
              label="Nombre"
              name={FirstNameDescription}
              rules={[
                {
                  required: true,
                  message: "Por favor ingrese el nombre del conductor",
                },
              ]}
            >
              <Input placeholder="Jane" />
            </Form.Item>
            <Form.Item
              label="Apellido"
              name={LastNameDescription}
              rules={[
                {
                  required: true,
                  message: "Por favor ingrese el apellido del conductor",
                },
              ]}
            >
              <Input placeholder="Doe" />
            </Form.Item>
          </InlineForm>
          <InlineForm>
            <Form.Item
              label="Placas del vehículo"
              name="plates"
              rules={[
                {
                  required: true,
                  message: "Por favor ingrese el numero de placas",
                },
              ]}
            >
              <Input placeholder="SNB1231" />
            </Form.Item>
            <Form.Item
              label="Modelo del vehículo"
              name="model"
              rules={[
                {
                  required: true,
                  message: "Por favor ingrese el modelo del vehículo",
                },
              ]}
            >
              <Input placeholder="Nissan March" />
            </Form.Item>
          </InlineForm>
          <Form.Item label="Contacto" name={MobileDescription}>
            <Input placeholder="2141547568" />
          </Form.Item>
          <Form.Item
            label="Calculo total del viaje"
            name="calculationType"
            rules={[{ required: true, message: "Seleccione un tipo" }]}
          >
            <Select
              placeholder="Seleccione un tipo"
              options={CALCULATION_TYPE_OPTIONS}
            />
          </Form.Item>
          <TotalTripField form={form} />
        </Form>
      </Modal>
    );
  };

  const map = useMemo(() => {
    return (
      !!pendingUserRequestValue.length && (
        <DispatcherMap
          destinations={getDestinationsByUserRequestHelper(
            pendingUserRequestValue[pendingUserRequestValue.length - 1],
          )}
          showProviders={true}
          providers={activeProviderListValue}
          assignProvider={handleSelectedProvider}
          selectedProvider={selectedProvider}
          setProvider={handleSelectedProviderFromMap}
        />
      )
    );
  }, [
    pendingUserRequestValue,
    activeProviderListValue,
    handleSelectedProvider,
    selectedProvider,
  ]);

  return (
    <ContainerLayout
      title={`Asignación de conductor para el ${pendingUserRequestValue[0].bookingId}`}
      showNotification={false}
      gutter={
        !!activeProviderListValue.length && status !== "loading"
          ? [32, 16]
          : undefined
      }
    >
      <Col span={24}>
        <Space>
          <Search
            placeholder="Buscar por nombre"
            allowClear
            onSearch={onSearch}
            loading={status === "loading"}
            disabled={status === "loading"}
          />
          <Button
            loading={confirmLoading}
            disabled={!selectedProvider}
            onClick={handleSelectedProvider}
          >
            Asignar conductor
          </Button>
          <Button
            loading={confirmLoading}
            onClick={handleModalOpen}
            type="primary"
          >
            Crear y asignar conductor temporal
          </Button>
        </Space>
      </Col>
      <Col span={8} style={{ paddingRight: 0 }}>
        {!activeProviderListValue.length && status !== "loading" && (
          <Col
            className="loading-container error-container"
            span={24}
            style={{ height: "100%" }}
          >
            <ExclamationCircleOutlined id="icon" />
            <Title level={2}>No hay datos</Title>
          </Col>
        )}
        {!!activeProviderListAsync.length && (
          <AutoSizer>
            {({ height, width }: Size) => (
              // @ts-ignore
              <List
                itemCount={activeProviderListValue.length}
                itemSize={100}
                height={height}
                width={width}
                itemData={activeProviderListValue}
              >
                {({ index, style, data }) => {
                  const item = data[index];
                  return (
                    <div
                      onClick={() => setSelectedProvider(item)}
                      key={item.id}
                      style={style}
                    >
                      <ProviderCard
                        provider={item}
                        selectedProviderId={selectedProvider?.id}
                        service={item?.services?.find(
                          (x) =>
                            x.serviceType?.id ===
                            pendingUserRequestValue[0]?.serviceTypeId,
                        )}
                      />
                    </div>
                  );
                }}
              </List>
            )}
          </AutoSizer>
        )}
      </Col>
      <Col span={16}>{map}</Col>
      <AssignTemporalDriverModal />
    </ContainerLayout>
  );
};

export default AssignProvider;
